import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FolderClosed, FolderOpen } from 'lucide-react';

import { ApiError } from '@/lib/api';
import { type FolderSummary, formatAmount, listMyFolders } from '@/lib/tickets-api';
import { cn } from '@/lib/utils';

/**
 * Pantalla del empleado: lista las carpetas de los viajes a los que RRHH
 * lo ha asignado. El empleado YA NO crea viajes — solo sube tickets a su
 * carpeta mientras el viaje esté abierto. Cuando RRHH cierra el viaje, la
 * carpeta pasa a "Cerrada" y deja de admitir tickets.
 */
export function Tickets() {
  const list = useQuery({
    queryKey: ['tickets', 'folders'],
    queryFn: () => listMyFolders(),
  });

  const folders = list.data ?? [];
  const open = folders.filter((f) => f.status === 'OPEN');
  const closed = folders.filter((f) => f.status === 'CLOSED');

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <header className="mb-5">
        <h1 className="text-xl md:text-2xl font-semibold tracking-tightish">Tickets de viaje</h1>
        <p className="text-[13px] text-muted-foreground mt-1">
          Sube las fotos de los tickets de los viajes a los que te ha asignado
          administración. Cuando el viaje termine, administración lo cierra.
        </p>
      </header>

      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : list.error ? (
        <p role="alert" className="text-sm text-destructive">
          {list.error instanceof ApiError ? list.error.message : 'Error cargando carpetas'}
        </p>
      ) : folders.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <FolderClosed className="h-8 w-8 mx-auto text-muted-foreground/60 mb-3" />
          <p className="text-sm text-muted-foreground">
            No tienes ningún viaje asignado ahora mismo. Cuando administración te
            asigne a uno, aparecerá aquí.
          </p>
        </div>
      ) : (
        <>
          <SectionList title="En curso" folders={open} emptyText="Sin viajes en curso." />
          {closed.length > 0 ? (
            <SectionList title="Cerradas" folders={closed} emptyText="" dimmed />
          ) : null}
        </>
      )}
    </div>
  );
}

function SectionList({
  title,
  folders,
  emptyText,
  dimmed,
}: {
  title: string;
  folders: FolderSummary[];
  emptyText: string;
  dimmed?: boolean;
}) {
  if (folders.length === 0 && !emptyText) return null;
  return (
    <section className="mb-6">
      <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground mb-2">
        {title}
      </p>
      {folders.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {folders.map((f) => (
            <li key={f.id}>
              <FolderCard folder={f} dimmed={dimmed} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FolderCard({ folder, dimmed }: { folder: FolderSummary; dimmed?: boolean }) {
  return (
    <Link
      to={`/tickets/${folder.id}`}
      className={cn(
        'flex items-center gap-3 bg-card border border-border rounded-sm px-4 py-3 hover:border-icaria-600 transition-colors',
        dimmed && 'opacity-70',
      )}
    >
      <div className="h-9 w-9 rounded-full bg-icaria-50 flex items-center justify-center text-icaria-600 flex-shrink-0">
        {folder.status === 'OPEN' ? (
          <FolderOpen className="h-4 w-4" />
        ) : (
          <FolderClosed className="h-4 w-4" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-[14.5px] truncate">{folder.name}</p>
        <p className="text-[12px] text-muted-foreground truncate">
          {folder.ticketCount} ticket{folder.ticketCount === 1 ? '' : 's'}
          {folder.totalCents > 0 ? ` · ${formatAmount(folder.totalCents)}` : ''}
        </p>
      </div>
      <span
        className={cn(
          'font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
          folder.status === 'OPEN'
            ? 'bg-success/10 border-success/40 text-success'
            : 'bg-muted border-border text-muted-foreground',
        )}
        style={{ letterSpacing: '0.06em' }}
      >
        {folder.status === 'OPEN' ? 'En curso' : 'Cerrada'}
      </span>
    </Link>
  );
}
